import React, { useEffect, useState } from 'react';
import { AlertTriangle, Printer } from 'lucide-react';
import { lireReglagesImpression } from '../../utils/imprimerRecu';

/**
 * L'avertissement des écrans d'encaissement (décision de Daniel du 04/11/2026).
 *
 * Quand l'école a réglé l'impression automatique, valider un paiement imprime
 * le reçu et, si le tiroir est réglé, ouvre le tiroir-caisse : un clic de trop
 * ouvre la caisse sans argent devant soi. Le bandeau le rappelle, à côté du
 * bouton qui valide, dans tous les parcours - paiement d'un élève, d'une
 * famille, ticket, inscription, réinscription, frais d'examen - et dit que
 * chaque ouverture est journalisée (RecuJournalService).
 *
 * Il porte aussi l'alerte du papier thermique, quand le stock compté passe
 * sous le seuil (page Reçus et impression, onglet Journal).
 *
 * Rien quand l'impression automatique est éteinte et que le papier va bien.
 */
const AvisEncaissement = ({ className = '' }) => {
  const [reglages, setReglages] = useState(null);

  useEffect(() => {
    let actif = true;
    lireReglagesImpression()
      .then((r) => { if (actif) setReglages(r); })
      .catch(() => {});
    return () => { actif = false; };
  }, []);

  if (!reglages) return null;
  const auto = Boolean(reglages.impression_auto);
  const tiroir = auto && Boolean(reglages.agent && reglages.ouvrir_tiroir);
  const papier = reglages.format !== 'a4' && reglages.papier?.alerte ? reglages.papier : null;
  if (!auto && !papier) return null;

  let messagePapier = null;
  if (papier) {
    messagePapier = Number(papier.jours_restants) > 0
      ? `Papier thermique : il reste environ ${papier.jours_restants} jour(s) de rouleaux. Prévenez la gestionnaire.`
      : 'Papier thermique : le stock compté est épuisé. Prévenez la gestionnaire.';
  }

  return (
    <div className={`w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 ${className}`} role="note">
      {auto && (
        <div className="flex items-start gap-2">
          <Printer size={14} className="mt-0.5 shrink-0 text-amber-700" />
          <p>
            {tiroir ? (
              <>
                <strong>Impression automatique et tiroir-caisse : </strong>
                à la validation d'un paiement, le reçu s'imprime et le tiroir-caisse s'ouvre. Chaque ouverture est
                journalisée (heure, caissier, reçu, montant) et contrôlée. Ne validez qu'un paiement réellement reçu,
                et rangez l'argent dans le tiroir.
              </>
            ) : (
              <>
                <strong>Impression automatique : </strong>
                à la validation d'un paiement, le reçu s'imprime. Chaque impression est journalisée : ne validez
                qu'un paiement réellement reçu.
              </>
            )}
          </p>
        </div>
      )}
      {messagePapier && (
        <div className={`flex items-start gap-2 ${auto ? 'mt-1.5 border-t border-amber-200 pt-1.5' : ''}`}>
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-700" />
          <p className="font-semibold">{messagePapier}</p>
        </div>
      )}
    </div>
  );
};

export default AvisEncaissement;
